import React, { useMemo, useState } from "react";
import { Settings2, Check, X } from "lucide-react";
import { pairUniverse, defaultPairs } from "../mock";
import TradingViewWidget from "./TradingViewWidget";

const STORAGE_KEY = "ws_pairs";
const CAT_ORDER = ["Crypto", "Metals", "Forex", "Indices"];

const LivePricesTV = () => {
  const [selected, setSelected] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {}
    return defaultPairs;
  });
  const [open, setOpen] = useState(false);

  const persist = (next) => {
    setSelected(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };
  const toggle = (id) =>
    persist(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const grouped = useMemo(() => {
    const g = {};
    pairUniverse.forEach((p) => { (g[p.cat] = g[p.cat] || []).push(p); });
    return g;
  }, []);

  // Build TradingView Market Quotes config from the user's selection.
  const { config, widgetKey, count } = useMemo(() => {
    const groups = {};
    selected.forEach((id) => {
      const p = pairUniverse.find((x) => x.id === id);
      if (!p) return;
      (groups[p.cat] = groups[p.cat] || []).push({ name: p.tv, displayName: p.label });
    });
    const symbolsGroups = CAT_ORDER
      .filter((c) => groups[c]?.length)
      .map((c) => ({ name: c, originalName: c, symbols: groups[c] }));
    const n = selected.length;
    const height = Math.max(260, n * 42 + symbolsGroups.length * 34 + 30);
    return {
      count: n,
      widgetKey: selected.join(","),
      config: {
        width: "100%",
        height,
        symbolsGroups,
        showSymbolLogo: true,
        isTransparent: true,
        colorTheme: "dark",
        locale: "en",
      },
    };
  }, [selected]);

  return (
    <div className="ws-panel p-6 relative">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[color:var(--ws-muted)]">
          <span className="ws-live-dot" /> Live Prices
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-[color:var(--ws-muted)] hidden sm:inline">Real-time · TradingView</span>
          <button onClick={() => setOpen((o) => !o)} className="ws-btn-ghost px-3 py-1.5 rounded-lg text-[12px] font-medium inline-flex items-center gap-2">
            <Settings2 className="w-3.5 h-3.5" /> Customize
          </button>
        </div>
      </div>

      {open && (
        <div className="ws-customize mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[13px] font-semibold text-white">Choose pairs to track</span>
            <button onClick={() => setOpen(false)} className="text-[color:var(--ws-muted)] hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAT_ORDER.map((cat) => (
              <div key={cat}>
                <div className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--ws-blue-bright)] mb-2">{cat}</div>
                <div className="space-y-1.5">
                  {(grouped[cat] || []).map((p) => {
                    const on = selected.includes(p.id);
                    return (
                      <button key={p.id} onClick={() => toggle(p.id)} className="flex items-center gap-2 w-full text-left group">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${on ? "bg-[color:var(--ws-blue)] border-[color:var(--ws-blue)]" : "border-[color:var(--ws-border-strong)]"}`}>
                          {on && <Check className="w-3 h-3 text-white" />}
                        </span>
                        <span className={`text-[13px] ${on ? "text-white" : "text-[color:var(--ws-muted)]"} group-hover:text-white transition-colors`}>{p.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {count === 0 ? (
        <div className="text-center py-10 text-[color:var(--ws-muted)] text-sm">
          No pairs selected. Click “Customize” to add some.
        </div>
      ) : (
        <div className="ws-tv-quotes">
          <TradingViewWidget
            key={widgetKey}
            scriptSrc="https://s3.tradingview.com/external-embedding/embed-widget-market-quotes.js"
            config={config}
          />
        </div>
      )}
    </div>
  );
};

export default LivePricesTV;
